import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Copy, Check, AlertTriangle, TrendingDown, CheckCircle2 } from "lucide-react";
import { supabase, type Existencia, type HistorialFila, type ZonaResumen } from "@/lib/supabase";
import { useAuth } from "@/auth/AuthProvider";
import { useAlmacen } from "@/lib/almacen";
import { cantidad, num, hace, fechaCorta, signo, etiquetaTipo, etiquetaMotivo } from "@/lib/formato";
import { Tarjeta, Badge, Esqueleto, ErrorBloque, Boton, Vacio, tonoEstado } from "@/components/ui";
import { mapearErrorSupabase } from "@/lib/errores";
import { traerTodo } from "@/lib/paginado";

export function Hoy() {
  const { perfil } = useAuth();
  const { almacen, multi } = useAlmacen();
  const modoErp = perfil?.empresa.modo_ventas === "erp";
  const fuente = modoErp ? "tu sistema" : "tu Excel";

  const ex = useQuery({
    queryKey: ["existencias", "hoy", almacen?.id],
    enabled: !!almacen,
    queryFn: async () => {
      return traerTodo((desde, hasta) => supabase.from("v_existencias").select("*").eq("almacen_id", almacen!.id).eq("activo", true).order("sku").range(desde, hasta));
    },
  });
  const movs = useQuery({
    queryKey: ["hoy", "movimientos", almacen?.id],
    enabled: !!almacen,
    queryFn: async () => {
      const { data, error } = await supabase.from("v_hoy_movimientos").select("*").eq("almacen_id", almacen!.id).order("fecha", { ascending: false }).limit(50);
      if (error) throw error;
      return data;
    },
  });
  const zonas = useQuery({
    queryKey: ["zonas", "resumen", almacen?.id],
    enabled: !!almacen,
    queryFn: async () => {
      const { data, error } = await supabase.from("v_zonas_ultimo_conteo").select("*").eq("almacen_id", almacen!.id).order("codigo");
      if (error) throw error;
      return data;
    },
  });

  const resumen = useMemo(() => armarResumen(ex.data ?? [], zonas.data ?? [], movs.data ?? []), [ex.data, zonas.data, movs.data]);
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    const texto = textoResumen({
      empresa: perfil?.empresa.nombre ?? "", almacen: multi ? almacen?.nombre : undefined, fuente, ...resumen,
    });
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      window.prompt("Copia el resumen:", texto);
    }
  }

  if (ex.error) return <ErrorBloque mensaje={mapearErrorSupabase(ex.error).mensaje} reintentar={() => ex.refetch()} />;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Hoy</h1>
          <p className="text-sm text-tinta-suave">
            {resumen.total > 0 ? <>{resumen.pctContado}% de tu catálogo ya fue contado alguna vez · {resumen.total} productos</> : "Sin productos todavía"}
            {modoErp && resumen.erpMasViejo != null && <> · {fuente} importado {hace(resumen.erpMasViejo)}</>}
          </p>
        </div>
        <Boton variante="secundario" onClick={copiar} disabled={ex.isLoading}>
          {copiado ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copiado ? "Copiado" : "Copiar resumen"}
        </Boton>
      </div>

      {ex.isLoading ? <Esqueleto filas={5} /> : resumen.total === 0 ? (
        <Tarjeta>
          <Vacio titulo="Todavía no hay productos" texto="Importa tu Excel o da de alta tus primeros SKUs para empezar a ver qué se acaba."
            accion={<div className="flex gap-2"><Link href="/importar"><Boton>Importar mi Excel</Boton></Link><Link href="/inventario"><Boton variante="secundario">Dar de alta un SKU</Boton></Link></div>} />
        </Tarjeta>
      ) : (
        <>
          {/* Qué se acaba */}
          <Tarjeta titulo={`Qué se acaba (${resumen.seAcaban.length})`}>
            {resumen.seAcaban.length === 0 ? (
              <div className="flex items-center gap-3 py-2 text-emerald-800"><CheckCircle2 className="h-6 w-6" /> <span>Todo en orden: nada bajo mínimo ni por acabarse.</span></div>
            ) : (
              <ul className="divide-y divide-borde">
                {resumen.seAcaban.slice(0, 25).map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{e.sku}</span>
                        <span className="truncate text-tinta-suave">{e.nombre}</span>
                        {e.estado === "agotado" ? <Badge tono="error">Agotado</Badge> : e.alerta_rotacion === "se_acaba" ? <Badge tono="error"><TrendingDown className="h-3 w-3" /> se acaba en {num(e.dias_cobertura)} días</Badge> : null}
                        {e.alerta_rotacion === "rota_rapido" && <Badge tono="alerta"><AlertTriangle className="h-3 w-3" /> rota más rápido de lo normal</Badge>}
                        {e.estado === "bajo" && e.alerta_rotacion !== "se_acaba" && <Badge tono={tonoEstado(e.estado)}>Bajo mínimo</Badge>}
                      </div>
                      <div className="text-xs text-tinta-suave">{e.ultimo_conteo_en ? `contado ${hace(e.ultimo_conteo_en)} por ${e.ultimo_conteo_por_nombre ?? "alguien"}` : `sin contar · dato de ${fuente}`}</div>
                    </div>
                    <div className="tabular text-right text-sm">
                      <div><span className="font-semibold">{cantidad(e.cantidad_fisica, e.unidad)}</span> <span className="text-tinta-suave">/ mín {num(e.stock_minimo)}</span></div>
                      {Number(e.sugerencia_compra) > 0 && <div className="text-tinta-suave">comprar {cantidad(e.sugerencia_compra, e.unidad)}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {resumen.sinMinimo > 0 && (
              <p className="mt-3 rounded bg-gray-50 px-3 py-2 text-sm text-tinta-suave">
                {resumen.sinMinimo} SKU{resumen.sinMinimo === 1 ? "" : "s"} sin mínimo: sin mínimo no puedo avisarte. <Link href="/inventario" className="text-primary underline">Ponerlos en Inventario</Link>
              </p>
            )}
          </Tarjeta>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Qué se movió hoy */}
            <Tarjeta titulo={`Qué se movió hoy (${movs.data?.length ?? 0})`}>
              {movs.isLoading ? <Esqueleto filas={3} /> : (movs.data?.length ?? 0) === 0 ? (
                <p className="py-2 text-tinta-suave">Nada registrado hoy todavía.</p>
              ) : (
                <ul className="divide-y divide-borde text-sm">
                  {movs.data!.slice(0, 15).map((m) => <FilaMovimiento key={m.id ?? ""} m={m} />)}
                </ul>
              )}
            </Tarjeta>

            {/* Zonas */}
            <Tarjeta titulo="Zonas y su último conteo">
              {zonas.isLoading ? <Esqueleto filas={3} /> : (
                <ul className="divide-y divide-borde text-sm">
                  {(zonas.data ?? []).filter((z) => (z.n_existencias ?? 0) > 0).map((z) => {
                    const dias = z.dias_sin_conteo;
                    const tono = dias == null ? "neutro" : dias === 0 ? "ok" : dias > 7 ? "error" : "alerta";
                    return (
                      <li key={z.zona_id ?? ""} className="flex items-center justify-between gap-2 py-2">
                        <Link href={`/contar/${z.zona_id}`} className="min-w-0 flex-1 hover:underline">
                          <span className="font-medium">{z.codigo}</span> <span className="text-tinta-suave">{z.nombre} · {z.n_existencias} SKUs</span>
                        </Link>
                        <Badge tono={tono}>{dias == null ? "sin contar" : dias === 0 ? `contada hoy por ${z.ultimo_conteo_por_nombre ?? "alguien"}` : `hace ${dias} d`}</Badge>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Tarjeta>
          </div>

          {modoErp && resumen.diferencias.length > 0 && (
            <Tarjeta titulo={`Diferencias entre lo físico y ${fuente}`}>
              <ul className="divide-y divide-borde text-sm">
                {resumen.diferencias.slice(0, 10).map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-2 py-2">
                    <span><span className="font-medium">{e.sku}</span> <span className="text-tinta-suave">{e.nombre}</span></span>
                    <span className="tabular">físico {num(e.cantidad_fisica)} / {fuente} {num(e.existencia_erp)} <strong className={Number(e.diferencia_erp) < 0 ? "text-red-700" : "text-emerald-700"}>({signo(e.diferencia_erp)})</strong></span>
                  </li>
                ))}
              </ul>
            </Tarjeta>
          )}
        </>
      )}
    </div>
  );
}

export function FilaMovimiento({ m }: { m: HistorialFila }) {
  const delta = Number(m.delta ?? 0);
  return (
    <li className="flex items-center justify-between gap-2 py-2">
      <span className="min-w-0">
        <span className="font-medium">{etiquetaTipo(m.tipo)}</span>
        {m.motivo && <span className="text-tinta-suave"> · {etiquetaMotivo(m.motivo)}</span>}
        <span className="block truncate text-tinta-suave">{m.sku} {m.producto_nombre} · {m.usuario_nombre} · {fechaCorta(m.fecha)}</span>
      </span>
      <span className={`tabular shrink-0 font-semibold ${delta < 0 ? "text-red-700" : delta > 0 ? "text-emerald-700" : "text-tinta-suave"}`}>{signo(delta)}</span>
    </li>
  );
}

// ---------- lógica del resumen (pura, testeable) ----------
export function armarResumen(ex: Existencia[], zonas: ZonaResumen[], movs: HistorialFila[]) {
  const orden = (e: Existencia) => {
    if (e.alerta_rotacion === "se_acaba") return 0;
    if (e.alerta_rotacion === "rota_rapido") return 1;
    if (e.estado === "bajo") return 2;
    if (e.estado === "agotado") return 3;
    return 9;
  };
  const seAcaban = ex.filter((e) => orden(e) < 9).sort((a, b) => {
    const d = orden(a) - orden(b);
    if (d !== 0) return d;
    const ca = a.dias_cobertura == null ? Infinity : Number(a.dias_cobertura);
    const cb = b.dias_cobertura == null ? Infinity : Number(b.dias_cobertura);
    if (ca !== cb) return ca - cb;
    return Number(b.sugerencia_compra ?? 0) - Number(a.sugerencia_compra ?? 0);
  });
  const total = ex.length;
  const contados = ex.filter((e) => e.ultimo_conteo_en).length;
  const diferencias = ex.filter((e) => e.diferencia_erp != null && Number(e.diferencia_erp) !== 0)
    .sort((a, b) => Math.abs(Number(b.diferencia_erp)) - Math.abs(Number(a.diferencia_erp)));
  const fechasErp = ex.map((e) => e.fecha_import_erp).filter(Boolean) as string[];
  const erpMasViejo = fechasErp.length ? fechasErp.sort()[0] : null;
  return {
    total, contados, pctContado: total ? Math.round((contados / total) * 100) : 0,
    seAcaban, diferencias, erpMasViejo,
    sinMinimo: ex.filter((e) => e.estado === "sin_minimo").length,
    zonasViejas: zonas.filter((z) => (z.n_existencias ?? 0) > 0 && (z.dias_sin_conteo == null || z.dias_sin_conteo > 7)),
    movsHoy: movs.length,
  };
}

export function textoResumen(r: ReturnType<typeof armarResumen> & { empresa: string; almacen?: string; fuente: string }): string {
  const hoy = new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
  const lineas: string[] = [];
  lineas.push(`EYE IA — ${r.empresa}${r.almacen ? ` — ${r.almacen}` : ""} — ${hoy}`);
  if (r.erpMasViejo) lineas.push(`${r.fuente} importado ${hace(r.erpMasViejo)}`);
  lineas.push(`${r.pctContado}% del catálogo contado · ${r.movsHoy} movimientos hoy`);
  lineas.push("");
  lineas.push(`SE ACABA / BAJO MÍNIMO (${r.seAcaban.length}):`);
  if (r.seAcaban.length === 0) lineas.push("  nada, todo en orden");
  for (const e of r.seAcaban.slice(0, 20)) {
    const etiqueta = e.estado === "agotado" ? "agotado" : e.alerta_rotacion === "se_acaba" ? `se acaba en ${num(e.dias_cobertura)} días` : e.alerta_rotacion === "rota_rapido" ? "rota más rápido de lo normal" : "bajo mínimo";
    const comprar = Number(e.sugerencia_compra) > 0 ? ` · comprar ${cantidad(e.sugerencia_compra, e.unidad)}` : "";
    lineas.push(`  ${e.sku} ${e.nombre}: ${cantidad(e.cantidad_fisica, e.unidad)} (mín ${num(e.stock_minimo)}) · ${etiqueta}${comprar}`);
  }
  if (r.diferencias.length) {
    lineas.push("");
    lineas.push(`MAYORES DIFERENCIAS vs ${r.fuente}:`);
    for (const e of r.diferencias.slice(0, 5)) lineas.push(`  ${e.sku} ${e.nombre}: físico ${num(e.cantidad_fisica)} / ${r.fuente} ${num(e.existencia_erp)} (${signo(e.diferencia_erp)})`);
  }
  if (r.zonasViejas.length) {
    lineas.push("");
    lineas.push(`ZONAS SIN CONTAR > 7 DÍAS: ${r.zonasViejas.map((z) => `${z.codigo}${z.dias_sin_conteo == null ? " (nunca)" : ` (${z.dias_sin_conteo} d)`}`).join(", ")}`);
  }
  return lineas.join("\n");
}
